/**
 * Notes Store (Zustand)
 *
 * Manages notes state: list, current note, backlinks, UI flags.
 * Wires the NotesPanel (Sprint 1.5) to the Sprint 2.8 API.
 */

import { create } from 'zustand';
import type { Note, NoteBlock } from '@signa/shared';
import { notesApi, type NoteSummary } from '../lib/notes-api';
import { DEMO_NOTES, DEMO_NOTE_SUMMARIES } from './demoData';

// Store state interface

interface NotesState {
  // Data
  notes: NoteSummary[];
  currentNote: Note | null;
  backlinks: Array<{ noteId: string; noteTitle: string }>;

  // UI
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  nextCursor: string | null;

  // Actions
  loadNotes: (params?: { limit?: number; cursor?: string }) => Promise<void>;
  loadMore: () => Promise<void>;
  loadNote: (id: string) => Promise<void>;
  createNote: (data: { title?: string; blocks: NoteBlock[] }) => Promise<void>;
  updateNote: (id: string, data: { title?: string; blocks?: NoteBlock[]; isPinned?: boolean; tags?: string[] }) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
  loadBacklinks: (noteId: string) => Promise<void>;
  loadDailyNote: (date: string) => Promise<void>;
  setCurrentNote: (note: Note | null) => void;
  clearError: () => void;
  loadDemo: () => void;
}

// Store

export const useNotesStore = create<NotesState>((set, get) => ({
  notes: [],
  currentNote: null,
  backlinks: [],
  isLoading: false,
  isSaving: false,
  error: null,
  nextCursor: null,

  clearError: () => set({ error: null }),

  loadNotes: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await notesApi.list(params);
      set({
        notes: res.notes,
        nextCursor: res.nextCursor ?? null,
        isLoading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load notes';
      set({ isLoading: false, error: message });
    }
  },

  loadMore: async () => {
    const { nextCursor } = get();
    if (!nextCursor) return;
    set({ isLoading: true, error: null });
    try {
      const res = await notesApi.list({ cursor: nextCursor });
      set((state) => ({
        notes: [...state.notes, ...res.notes],
        nextCursor: res.nextCursor ?? null,
        isLoading: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load more notes';
      set({ isLoading: false, error: message });
    }
  },

  loadNote: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const res = await notesApi.get(id);
      set({ currentNote: res.note, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load note';
      set({ isLoading: false, error: message });
    }
  },

  createNote: async (data) => {
    set({ isSaving: true, error: null });
    try {
      const res = await notesApi.create(data);
      const summary: NoteSummary = {
        id: res.note.id,
        title: res.note.title,
        snippet: blocksToSnippet(res.note.blocks),
        updatedAt: res.note.updatedAt,
        labels: res.note.tags,
        isPinned: res.note.isPinned,
        isDaily: res.note.isDaily,
      };
      set((state) => ({
        notes: [summary, ...state.notes],
        currentNote: res.note,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create note';
      set({ isSaving: false, error: message });
    }
  },

  updateNote: async (id, data) => {
    set({ isSaving: true, error: null });
    try {
      const res = await notesApi.update(id, data);
      const summary: NoteSummary = {
        id: res.note.id,
        title: res.note.title,
        snippet: blocksToSnippet(res.note.blocks),
        updatedAt: res.note.updatedAt,
        labels: res.note.tags,
        isPinned: res.note.isPinned,
        isDaily: res.note.isDaily,
      };
      set((state) => ({
        notes: state.notes.map((n) => (n.id === id ? summary : n)),
        currentNote: res.note,
        isSaving: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save note';
      set({ isSaving: false, error: message });
    }
  },

  deleteNote: async (id: string) => {
    set({ error: null });
    try {
      await notesApi.delete(id);
      set((state) => {
        const remaining = state.notes.filter((n) => n.id !== id);
        const newCurrent = state.currentNote?.id === id ? null : state.currentNote;
        return { notes: remaining, currentNote: newCurrent };
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete note';
      set({ error: message });
    }
  },

  loadBacklinks: async (noteId: string) => {
    try {
      const res = await notesApi.getBacklinks(noteId);
      set({ backlinks: res.backlinks });
    } catch {
      set({ backlinks: [] });
    }
  },

  loadDailyNote: async (date: string) => {
    set({ isLoading: true, error: null });
    try {
      const res = await notesApi.getDailyNote(date);
      if (res.note) {
        set((state) => {
          const summary: NoteSummary = {
            id: res.note!.id,
            title: res.note!.title,
            snippet: blocksToSnippet(res.note!.blocks),
            updatedAt: res.note!.updatedAt,
            labels: res.note!.tags,
            isPinned: res.note!.isPinned,
            isDaily: res.note!.isDaily,
          };
          const exists = state.notes.some((n) => n.id === res.note!.id);
          return {
            notes: exists
              ? state.notes.map((n) => (n.id === summary.id ? summary : n))
              : [summary, ...state.notes],
            currentNote: res.note,
            isLoading: false,
          };
        });
      } else {
        // No daily note for this date - create one
        const title = formatDailyTitle(date);
        const dailyBlocks: NoteBlock[] = [
          { id: `b-${Date.now()}-0`, type: 'heading1', content: title },
          { id: `b-${Date.now()}-1`, type: 'paragraph', content: '' },
        ];
        const createRes = await notesApi.create({ title, blocks: dailyBlocks });
        const summary: NoteSummary = {
          id: createRes.note.id,
          title: createRes.note.title,
          snippet: '',
          updatedAt: createRes.note.updatedAt,
          labels: [],
          isPinned: createRes.note.isPinned,
          isDaily: createRes.note.isDaily,
        };
        set((state) => ({
          notes: [summary, ...state.notes],
          currentNote: createRes.note,
          isLoading: false,
        }));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load daily note';
      set({ isLoading: false, error: message });
    }
  },

  setCurrentNote: (note) => set({ currentNote: note }),

  loadDemo: () => {
    set({
      notes: DEMO_NOTE_SUMMARIES,
      currentNote: DEMO_NOTES[0] ?? null,
      backlinks: [],
      isLoading: false,
      isSaving: false,
      error: null,
      nextCursor: null,
    });
  },
}));

// Helpers

function blocksToSnippet(blocks: NoteBlock[]): string {
  const textBlock = blocks.find(
    (b) => b.type === 'paragraph' && b.content.trim().length > 0
  );
  const raw = textBlock?.content ?? '';
  return raw.length > 120 ? `${raw.slice(0, 120)}...` : raw;
}

export function formatDailyTitle(dateStr: string): string {
  // dateStr is YYYY-MM-DD
  const [yearStr, monthStr, dayStr] = dateStr.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// Demo Mode
if (typeof window !== 'undefined') {
  window.addEventListener('signa:demo-mode', () => {
    const state = useNotesStore.getState();
    if (state.notes.length === 0) {
      useNotesStore.getState().loadDemo();
    }
  });
}
